import {
  BaseCompanyIdProps,
  ProjectId,
  UserId,
  UUID,
} from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

/** List of the transaction sources, From which application a request has been mane to create a a transaction */
export enum TransactionSources {
  sphereDashboard = "SphereDashboard",
  webEditor = "WebEditor",
  sphereViewer = "SphereViewer",
  coreApi = "core-api",
}

/** Information about a transaction source application */
export interface CreditTransactionSource {
  /** Name of a transaction source application */
  type: TransactionSources;

  /** Version of the source application */
  version: string;
}

/** List of transaction type */
export enum TransactionTypes {
  /** Transaction action type related to token consumption  */
  consumeTokens = "ConsumeTokens",

  /** Transaction action type related to token insertion by purchasing or from admin panel */
  insertTokens = "InsertTokens",

  /** Action types for expired token transactions */
  expireTokens = "ExpireTokens",
}

/** All the types of transaction result */
export type CreditTransactionResultType =
  | "TokensInserted"
  | "TokenInsertionRejected"
  | "TokenInsertionFailed"
  | "TokensConsumed"
  | "TokenConsumptionRejected"
  | "TokenConsumptionFailed"
  | "TokensExpired";

/** List of reasons for credit consumption */
export enum ConsumptionReason {
  floorplanGeneration = "floorplan-generator",
}

/** The action for the credit transaction when tokens are inserted */
export type CreditInsertAction = {
  /** The type of the action */
  type: TransactionTypes.insertTokens;

  /** The data of the action */
  data: {
    /** Amount of the token that were inserted */
    tokenAmount: number;

    /** Reason for the insertion of the tokens */
    reason: ConsumptionReason;

    /** The cause/trigger of inserting tokens, granted from admin panel or purchased. */
    trigger: "granted" | "purchased";
  };
};

/** The action for the credit transaction when tokens are consumed */
export type CreditConsumeAction = {
  /** The type of the action */
  type: TransactionTypes.consumeTokens;

  /** The data of the action */
  data: {
    /** The amount of tokens that were consumed. */
    tokenAmount: number;

    /** Reason for the consumption of the token */
    consumptionReason: ConsumptionReason;
  };
};

/** The action for the credit transaction when tokens are expired */
export type CreditExpireAction = {
  /** The type of the action */
  type: TransactionTypes.expireTokens;

  /** The data of the action */
  data: {
    /** The reason for the expiration. */
    expirationReason: string | null;
  };
};

/** Information about a transaction action, Purpose of the transaction */
export type CreditTransactionAction =
  | CreditInsertAction
  | CreditConsumeAction
  | CreditExpireAction;

/** Additional information about a transaction */
export interface TransactionContext extends BaseCompanyIdProps {
  /** The id of the user who triggered the request to create a credit transaction */
  userId?: UserId | null;

  /** Used as fallback and initial ui value until user id resolved */
  userEmail?: string | null;

  /** The user for whom the event was created. */
  onBehalfOfUser?: UserId | null;

  /** The id of the project for which the transaction has been processed */
  projectId?: ProjectId | null;

  /** The id of any other activity events, can be grouped multiple activity */
  correlationId?: string | null;
}

/** The base data for the result of a credit transaction */
type BaseResultData = {
  /**
   * For consumption type The IDs of the subscriptions that were affected by the credit consumption.
   * For insertion type The ID of the subscription the tokens were added to.
   * For expiration type, The ID of the subscription which expired or was deactivated.
   */
  subscriptionIds: string[];
};

/** The result of a credit transaction when tokens are inserted */
interface CreditInsertedResult {
  /** The type of the result */
  type: "TokensInserted";

  /**
   * The outcome of the action, implicitly determined by the result type.
   * Read-only - populated by the service based on the event result.
   */
  outcome?: string;

  /** Additional information about context data */
  data: {
    /** The ID of the subscription the tokens were added to. */
    subscriptionId: string | null;
  };
}

/** The result of a credit transaction when token insertion are rejected */
interface CreditInsertionRejectedResult {
  /** The type of the result */
  type: "TokenInsertionRejected";

  /**
   * The outcome of the action, implicitly determined by the result type.
   * Read-only - populated by the service based on the event result.
   */
  outcome?: string;

  /** Additional information about context data */
  data: { rejectionReason: string };
}

/** The result of a credit transaction when token insertion failed */
interface CreditInsertionFailedResult {
  /** The type of the result */
  type: "TokenInsertionFailed";

  /** Additional information about context data */
  data: {
    /** The reason for the failure of the credit insertion */
    failureReason: string;

    /** The details of the failure of the credit insertion */
    failureDetails?: string | null;
  };
}

/** The result of a credit transaction when tokens are consumed */
interface CreditConsumedResult {
  /** The type of the result */
  type: "TokensConsumed";

  /**
   * The outcome of the action, implicitly determined by the result type.
   * Read-only - populated by the service based on the event result.
   */
  outcome?: string;

  /** Additional information about context data */
  data: BaseResultData;
}

/** The result of a credit transaction when token consumption is rejected */
interface CreditConsumptionRejectedResult {
  /** The type of the result */
  type: "TokenConsumptionRejected";

  /**
   * The outcome of the action, implicitly determined by the result type.
   * Read-only - populated by the service based on the event result.
   */
  outcome?: string;

  /** Additional information about context data */
  data: {
    /** The reason for the rejection of the credit consumption */
    rejectionReason: string;
  };
}

/** The result of a credit transaction when token consumption failed */
interface CreditConsumptionFailedResult {
  /** The type of the result */
  type: "TokenConsumptionFailed";

  /**
   * The outcome of the action, implicitly determined by the result type.
   * Read-only - populated by the service based on the event result.
   */
  outcome?: string;

  /** Additional information about context data */
  data: {
    /** The details of the failure of the credit consumption */
    failureReason: string;

    /** The details of the failure of the credit consumption */
    failureDetails?: string | null;
  };
}

/** The result of a credit transaction when tokens are expired */
interface CreditExpiredResult {
  /** The type of the result */
  type: "TokensExpired";

  /**
   * The outcome of the action, implicitly determined by the result type.
   * Read-only - populated by the service based on the event result.
   */
  outcome?: string;

  /** Additional information about context data */
  data: {
    /** The ID of the subscription which expired or was deactivated. */
    subscriptionId: string | null;

    /** The amount of tokens that expired. */
    expiredAmount: number;
  };
}

/** Information that all type of transaction has */
type BaseCreditTransaction = {
  /** The id of the transaction */
  id: UUID;

  /** Timestamp of transaction creation */
  timestamp: string;

  /** Information about the source application from where the transaction is created */
  source: CreditTransactionSource;

  /** Additional information about the transaction */
  context: TransactionContext;

  /**
   * Tags that can be used to filter and categorize actions.
   * Read-only - populated by the service based on the action id and category pair.
   */
  tags: string[];

  /**
   * The visibility of the action. By default, only external actions are visible to clients.
   * Read-only - populated by the service based on the action id and category pair.
   */
  visibility: "Internal" | "External";
};

/** Information about transactions which are related to token addition */
export type CreditInsertTransaction = BaseCreditTransaction & {
  /** Information about a transaction action, Purpose of the transaction */
  action: CreditInsertAction;

  /** The result of a credit transaction when tokens are inserted */
  result:
    | CreditInsertedResult
    | CreditInsertionRejectedResult
    | CreditInsertionFailedResult;
};

/** Information about transactions which are related to token consumption */
export type CreditConsumeTransaction = BaseCreditTransaction & {
  /** Information about a transaction action, Purpose of the transaction */
  action: CreditConsumeAction;

  /** The result of a credit transaction when tokens are consumed */
  result:
    | CreditConsumedResult
    | CreditConsumptionRejectedResult
    | CreditConsumptionFailedResult;
};

/** Information about transactions which are related to token expiration */
export type CreditExpiredTransaction = BaseCreditTransaction & {
  /** Information about a transaction action, Purpose of the transaction */
  action: CreditExpireAction;

  /** The result of a credit transaction when tokens are consumed */
  result: CreditExpiredResult;
};

/** Information about all transactions type */
export type CreditTransaction =
  | CreditInsertTransaction
  | CreditConsumeTransaction
  | CreditExpiredTransaction;

export interface CreditSubscription {
  /**
   * The available credit of a subscription
   * This is the difference between the maximum credit and the used credit.
   */
  availableCredit: number;

  /** The expiration date of a subscription */
  expiresAt: FaroCoreApiTypes.Iso8601Date;

  /** The start date of a subscription */
  startDate: FaroCoreApiTypes.Iso8601Date;

  /** The maximum credit of a subscription */
  max: number;

  /** The amount of credit used so far */
  current: number;

  /**
   * A unique identifier to identify a credit subscription object
   * This is used to identify the object in the UI and is not coming from the backend.
   */
  uiTemporaryId: UUID;
}

export interface ExpiringCredits {
  /**
   * The sum of available credits from all subscriptions that share the same expiration date.
   */
  totalCredits: number;

  /** The expiration date */
  expiresAt: string;
}

/** The available view modes for the credit dialog. */
export enum CreditViewMode {
  transaction = "Transactions",
  purchases = "Purchases",
}

export enum CreditSubscriptionStatus {
  /** Means the credits remaining are the same as the purchased credits */
  active = "Active",

  /** Means the credits remaining are more than 0 but less than the purchased credits */
  activeInUse = "Active - In Use",

  /** The expiration date is later than the current date but the credits remaining is 0 */
  used = "Used",

  /** The expiration date is later than the current date */
  expired = "Expired",
}
